import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CANADIAN_PROVINCES, COUNTRY_CODES, US_STATES } from '@/config/billing'
import { isValidCanadianProvinceCode, isValidStateCode } from '@/lib/utils'

type Props = {
  isDisabled: boolean
}

export const StateFormField = ({ isDisabled }: Props) => {
  const form = useFormContext()
  const { watch } = form
  const country = watch('country')
  const stateValue = watch('state')
  const [open, setOpen] = useState(false)

  const isInUs = country === COUNTRY_CODES.US
  const isInCanada = country === COUNTRY_CODES.CA

  const usStateList = useMemo(
    () =>
      Object.entries(US_STATES).map(([code, name]) => ({
        code,
        name,
      })),
    []
  )

  const canadaProvinceList = useMemo(
    () =>
      Object.entries(CANADIAN_PROVINCES).map(([code, name]) => ({
        code,
        name,
      })),
    []
  )

  const getStateLabel = () => {
    if (country === COUNTRY_CODES.UK) return 'County'
    if (country === COUNTRY_CODES.CA) return 'Province'
    return 'State'
  }

  const getSelectedDisplayName = () => {
    if (!stateValue) return ''
    if (isInUs) {
      const stateName = US_STATES[stateValue as keyof typeof US_STATES]
      return stateName || String(stateValue)
    }
    if (isInCanada) {
      const provinceName =
        CANADIAN_PROVINCES[stateValue as keyof typeof CANADIAN_PROVINCES]
      return provinceName || String(stateValue)
    }
    return String(stateValue)
  }

  const validateState = (value: unknown) => {
    if (!value) return true

    const stringValue = String(value)
    if (isInUs && !isValidStateCode(stringValue)) {
      return 'Invalid US state code'
    }
    if (isInCanada && !isValidCanadianProvinceCode(stringValue)) {
      return 'Invalid Canadian province code'
    }
    return true
  }

  return (
    <FormField
      control={form.control}
      name="state"
      disabled={isDisabled}
      rules={{
        validate: validateState,
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{getStateLabel()}</FormLabel>
          <>
            {isInUs ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isDisabled}
                      className="w-full justify-between text-left font-normal"
                    >
                      {getSelectedDisplayName() || 'Select state'}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search state..." />
                    <CommandList>
                      <CommandEmpty>No state found.</CommandEmpty>
                      <CommandGroup>
                        {usStateList.map(({ code, name }) => (
                          <CommandItem
                            key={code}
                            value={name}
                            onSelect={() => {
                              form.setValue('state', code)
                              setOpen(false)
                            }}
                          >
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : isInCanada ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isDisabled}
                      className="w-full justify-between text-left font-normal"
                    >
                      {getSelectedDisplayName() || 'Select province'}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search province..." />
                    <CommandList>
                      <CommandEmpty>No province found.</CommandEmpty>
                      <CommandGroup>
                        {canadaProvinceList.map(({ code, name }) => (
                          <CommandItem
                            key={code}
                            value={name}
                            onSelect={() => {
                              form.setValue('state', code)
                              setOpen(false)
                            }}
                          >
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <FormControl>
                <Input
                  placeholder="Enter state/province"
                  aria-label="State or province"
                  disabled={isDisabled}
                  {...field}
                />
              </FormControl>
            )}
          </>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
